import React, { useEffect, useCallback, useMemo, useState } from 'react'
import apiClient from '../utils/api/apiClient'
import { APIEndpoints } from '../utils/api/apiConfig'
import { emptyGuid, isEmptyGuid } from '../utils/emptyGuid'
import { useLocalReceiptsState } from './LocalReceiptsProvider'
import { emptyDate, isEmptyDate } from '../utils/emptyDate'
import { cloneDeep } from 'lodash';
import { setValueByKey } from '../utils/utils'
import { setPointerErrorsFromApiResponse } from "../utils/api/apiUtils"
import { newGuid } from '../utils/objectutils'
import { useTranslation } from "react-i18next";

const LocalReceiptDataContext = React.createContext({})
export function useLocalReceiptDataState() {
  return React.useContext(LocalReceiptDataContext)
}

const positionsTemplate = []
const positionTemplate = {
  isNewPosition: true,
  id: emptyGuid,
  batchkey: "",
  serialkey: "",
  expiryDate: emptyDate,
  warrantyDate: emptyDate,
  quantityExpected: 1,
  quantityCounted: 0,
  metaProperties: [],
  validation: [],
  article: {
    id: emptyGuid,
    useSerialKeys: false,
    useBatchKeys: false,
    isWithSizes: true,
    isImmaterial: false,
    displayName: "",
    articleNumber: "",
  },
  articleVariant: {
    id: emptyGuid
  }
}
const headerDataTemplate = {
  id: emptyGuid,
  displayName: '',
  comment: '',
  expectedTimeOfArrival: '',
  supplier: {
    id: emptyGuid
  },
  targetStorehouse: {
    id: emptyGuid
  }
}

const LocalReceiptDataProvider = ({ children }) => {
  const [splitPositionId, setSplitPositionId] = useState(null)
  const [successSubmit, setSuccessSubmit] = useState(false)
  const [headerData, setHeaderData] = useState(cloneDeep(headerDataTemplate))
  const [initialHeaderData, setInitialHeaderData] = useState('')

  const [positions, setPositions] = useState(cloneDeep(positionsTemplate))
  const [initialPositions, setInitialPositions] = useState('')
  const [actionMode, setActionMode] = useState('create')
  const [formErrors, setFormErrors] = useState({})
  const [errorsOccurredOnSubmit, setErrorsOccurredOnSubmit] = useState(false)

  const [loadedReceiptData, setLoadedReceiptData] = useState(null)
  const { setForceReloadLocalReceipts } = useLocalReceiptsState()
  const [storingResult, setStoringResult] = useState({})
  const [subsequentDeliveryResult, setSubsequentDeliveryResult] = useState({})
  const { t } = useTranslation();
  const setHeaderDataByKey = useCallback((key, value) => {
    setHeaderData(prev => setValueByKey(key, value, prev))
  }, [])
  const setPositionsByKey = useCallback((key, value) => {
    setPositions(prev => setValueByKey(key, value, prev))
  }, [])

  const resetErrorByFormKey = useCallback(formKey => {
    setFormErrors(prev => {
      if (prev[formKey]) {
        delete prev[formKey]
      }
      return prev
    })
  }, [setFormErrors])

  const loadReceiptData = useCallback(async (id, mode) => {
    setActionMode(mode)
    let loadedData = await apiClient.getJson(APIEndpoints.localGoodsReceipts(id, mode).plannedReceipts)
    setLoadedReceiptData(loadedData)
    initLoadedReceiptData(cloneDeep(loadedData));
  }, [])

  const initLoadedReceiptData = (loadedData) => {
    const loadedHeaderData = loadedData.header
    const loadedPositions = loadedData.positions || []

    // init headerData fields
    const newHeaderData = {
      ...loadedHeaderData,
      supplier: loadedHeaderData.supplier ? loadedHeaderData.supplier : { id: emptyGuid },
      targetStorehouse: loadedHeaderData.targetStorehouse ? loadedHeaderData.targetStorehouse : { id: emptyGuid },
    };
    setHeaderData(newHeaderData)
    setInitialHeaderData(JSON.stringify(newHeaderData));

    // init positions
    const newPositions = [...loadedPositions]
    setPositions(newPositions)
    setInitialPositions(JSON.stringify(newPositions))
  }

  const headerDataHasChanged = useMemo(() => {
    if (initialHeaderData !== '') {
      const headerDataString = JSON.stringify(headerData)
      return headerDataString !== initialHeaderData
    }
  }, [headerData, initialHeaderData])

  const positionsHasChanged = useMemo(() => {
    if (initialPositions !== '') {
      const positionsString = JSON.stringify(positions)
      return positionsString !== initialPositions
    }
  }, [positions, initialPositions])



  const formDataHasChanged = useMemo(() => {
    return headerDataHasChanged || positionsHasChanged
  }, [headerDataHasChanged, positionsHasChanged])

  const resetFormData = useCallback(() => {
    setHeaderData(JSON.parse(initialHeaderData))
    setPositions(JSON.parse(initialPositions))
    setFormErrors({})
    setErrorsOccurredOnSubmit(false)
  }, [setFormErrors, setErrorsOccurredOnSubmit, initialHeaderData, initialPositions])

  const getPreparedHeaderData = useCallback(() => {
    const preparedHeaderData = cloneDeep(headerData);
    //prepare dates
    ["expectedTimeOfArrival"].forEach(dateProp => {
      if (isEmptyDate(preparedHeaderData[dateProp])) {
        preparedHeaderData[dateProp] = emptyDate
      }
    })
    return preparedHeaderData
  }, [headerData])


  const getPreparedPositions = useCallback(() => {
    const preparedPositions = cloneDeep(positions)
    preparedPositions.forEach(item => {
      // change all id's to emptyGuids for the new positions (we have to send out empty guids when saving)
      if (item.isNewPosition) {
        item.id = emptyGuid
        delete item.isNewPosition
      }
      if (isEmptyDate(item.expiryDate)) {
        item.expiryDate = emptyDate
      }
      if (isEmptyDate(item.warrantyDate)) {
        item.warrantyDate = emptyDate
      }

      delete item.validation
      delete item.metaProperties
    })
    return preparedPositions
  }, [positions])

  const submitAddReceipt = useCallback(async () => {
    setFormErrors({})
    setErrorsOccurredOnSubmit(false)
    const preparedData = { header: getPreparedHeaderData(), positions: getPreparedPositions() }
    try {
      const createdReceiptId = await apiClient.postJson(APIEndpoints.localGoodsReceipts().plannedReceipts, preparedData)
      setSuccessSubmit(true)
      return { createdReceiptId, receiptName: preparedData.header.displayName }
    } catch (e) {
      setErrorsOccurredOnSubmit(true)
      setFormErrors(setPointerErrorsFromApiResponse(e))
    }
    console.log("submitAddReceipt", formErrors)
    return null
  }, [getPreparedHeaderData, getPreparedPositions, formErrors])

  const submitEditReceipt = async () => {
    setFormErrors({})
    setErrorsOccurredOnSubmit(false)
    const preparedData = {
      header: getPreparedHeaderData(),
      positions: getPreparedPositions()
    }
    const updatedData = {
      mode: actionMode,
      receipt: preparedData
    }
    try {
      await apiClient.putJson(APIEndpoints.localGoodsReceipts().plannedReceipts, updatedData)
      setSuccessSubmit(true)
      await loadReceiptData(headerData.id, actionMode)
      setForceReloadLocalReceipts(true)
    } catch (e) {
      setErrorsOccurredOnSubmit(true)
      setFormErrors(setPointerErrorsFromApiResponse(e))
    }
  }
  const submitStoreReceipt = async () => {
    setFormErrors({})
    setErrorsOccurredOnSubmit(false)
    if (headerDataHasChanged) {
      return
    }
    if (positionsHasChanged) {
      return
    }
    try {
      let resultStoring = await apiClient.postJson(APIEndpoints.localGoodsReceipts(headerData.id).storedPlannedReceipts, {})
      setStoringResult(resultStoring)
      setSuccessSubmit(true)
      await loadReceiptData(headerData.id, actionMode)
      setForceReloadLocalReceipts(true)
    } catch (e) {
      setErrorsOccurredOnSubmit(true)
      setFormErrors(setPointerErrorsFromApiResponse(e))
    }
  }
  const addPosition = useCallback(async (articleId) => {
    setFormErrors({})
    try {
      const loadArticle = await apiClient.getJson(APIEndpoints.article(articleId))
      if (loadArticle) {
        const newPosistions = [...positions]
        const clonedTemplate = cloneDeep(positionTemplate)
        clonedTemplate.id = newGuid()
        let newArticle = {
          id: loadArticle.id,
          useSerialKeys: loadArticle.useSerialKeys,
          useBatchKeys: loadArticle.useBatchKeys,
          isWithSizes: loadArticle.isWithSizes,
          isWithExpiry: loadArticle.isWithExpiry,
          isWithWarranty: loadArticle.isWithWarranty,
          isImmaterial: loadArticle.isImmaterial,
          displayName: loadArticle.displayName,
          articleNumber: loadArticle.articleNumber,
        }
        clonedTemplate.article = newArticle
        newPosistions.push(clonedTemplate)
        setPositions(newPosistions)
      }
    } catch (e) {
      setFormErrors(setPointerErrorsFromApiResponse(e))
    }
    return null
  }, [positions])

  const checkInstance = useCallback(async (positionId, changedKey) => {
    if (changedKey !== 'serialkey' && changedKey !== 'batchkey') return false
    const foundIndex = positions.findIndex(item => item.id === positionId)
    if (foundIndex < 0) return false
    const position = positions[foundIndex]
    if (!position) return null
    const { article, articleVariant } = position
    if (!article.useSerialKeys && !article.useBatchKeys) return false
    if (!article.isWithWarranty && !article.isWithExpiry) return false
    let loadedInstance = null
    if (position.serialkey || position.batchkey) {
      setFormErrors({})
      try {
        let params = {
          'id.articleId': article.id,
          'id.sizeId': articleVariant?.id,
          'id.serialkey': position.serialkey,
          'id.batchKey': position.batchkey,
        }
        loadedInstance = await apiClient.getJson(
          APIEndpoints.localstock().instance,
          params,
        )
      } catch (e) {
        setFormErrors(setPointerErrorsFromApiResponse(e))
      }
    }
    return loadedInstance
  }, [positions])
  const copyPosition = useCallback(async (positionId) => {
    const position = positions.find(item => item.id === positionId)
    if (position) {
      const newPosistions = [...positions]
      const clonedPosition = cloneDeep(position)
      clonedPosition.id = newGuid()
      clonedPosition.serialkey = ""
      clonedPosition.expiryDate = emptyDate
      clonedPosition.warrantyDate = emptyDate
      clonedPosition.hasInstance = false
      clonedPosition.isNewPosition = true
      clonedPosition.metaProperties = []
      //clonedPosition.validation= []
      newPosistions.push(clonedPosition)
      setPositions(newPosistions)
    }
  }, [positions])
  const splitPosition = useCallback(async (positionId, splitQuantity = 1) => {
    const foundIndex = positions.findIndex(item => item.id === positionId)
    if (foundIndex >= 0) {
      let splitPosition = positions[foundIndex]
      const newPosistions = [...positions]
      const clonedPosition = cloneDeep(splitPosition)
      clonedPosition.quantityExpected = clonedPosition.quantityExpected - splitQuantity
      if (clonedPosition.quantityExpected <= 0) {
        //quantitity is not splitable
        return
      }
      clonedPosition.isSplitFromPositionId = clonedPosition.id
      clonedPosition.id = newGuid()
      clonedPosition.serialkey = ""
      clonedPosition.expiryDate = emptyDate
      clonedPosition.warrantyDate = emptyDate
      clonedPosition.hasInstance = false
      clonedPosition.isNewPosition = true
      clonedPosition.metaProperties = []
      //clonedPosition.validation= []
      if (clonedPosition.quantityCounted > 0) {
        clonedPosition.quantityCounted = clonedPosition.quantityCounted - splitQuantity
      }
      if (clonedPosition.article.useRFIDs || clonedPosition.article.useSerialKeys) {
        clonedPosition.quantityCounted = 0
      }
      //newPosistions.push(clonedPosition)
      //change quantities of old position
      newPosistions[foundIndex].quantityCounted = splitQuantity
      newPosistions[foundIndex].quantityExpected = splitQuantity
      newPosistions[foundIndex].enforceUpdate = true

      let firstPositions = newPosistions.slice(0, foundIndex + 1);
      let lastPositions = newPosistions.slice(foundIndex + 1);
      firstPositions.push(clonedPosition)

      let mergedPositions = firstPositions.concat(lastPositions);
      setPositions(mergedPositions)
    }
  }, [setPositions, positions])
  const checkPositionHasOpenQuantity = (pos) => {
    if ((pos.article.useRFIDs || pos.article.useSerialKeys) && pos.quantityCounted > 0 && pos.quantityExpected > 1)
      return true
    return pos.quantityExpected > pos.quantityCounted
  }
  const addScannedArticle = useCallback(async (scanResponse) => {
    let { article, articleVariant } = scanResponse
    if (scanResponse.isWithSizes && isEmptyGuid(articleVariant?.id)) {
      throw new Error(t('inventory:invalidScanResult'))
    }
    if (!positions.some(pos => checkPositionHasOpenQuantity(pos))) {
      throw new Error(t('picking:allPositionsAreAlreadyScanned'))
    }
    const result = []
    for (let i = 0; i < positions.length; i++) {
      if (positions[i].article.id === article.id) {
        result.push({
          data: positions[i],
          index: i,
          hasOpenQuantity: checkPositionHasOpenQuantity(positions[i]),
        })
      }
    }
    if (result?.length) {
      let foundobj = null;
      if (scanResponse.useSerialKeys && scanResponse.serialKey) {
        foundobj = result.find(item => item.data.serialkey === scanResponse.serialKey)
        if (!!foundobj && !foundobj.hasOpenQuantity)
          throw new Error(t('picking:positionsIsAlreadyScanned'))
      }
      else if (scanResponse.useRFIDs && scanResponse.rfid) {
        foundobj = result.find(item => item.data.rfid === scanResponse.rfid)
        if (!!foundobj && !foundobj?.hasOpenQuantity)
          throw new Error(t('picking:positionsIsAlreadyScanned'))
      }
      else if (scanResponse.useBatchKeys && scanResponse.batchKey) {
        foundobj = result.find(item => item.data.batchkey === scanResponse.batchKey && item.hasOpenQuantity)
      }
      if (!foundobj) {
        let result2 = result.filter(item => item.hasOpenQuantity)
        if (scanResponse.isWithSizes) {
          foundobj = result2.find(item => item.data.articleVariant?.id === articleVariant?.id)
        }
        else if (result2.length) {
          foundobj = result2[0]
        }
      }
      if (foundobj) {
        let mappedPosition = cloneDeep(foundobj.data)
        if (mappedPosition.article.useSerialKeys && scanResponse.serialKey) {
          mappedPosition.serialkey = scanResponse.serialKey
        }
        else if (mappedPosition.article.useBatchKeys && scanResponse.batchKey) {
          mappedPosition.batchkey = scanResponse.batchKey
        }
        if (mappedPosition.article.useSerialKeys || mappedPosition.article.useRFIDs) {
          mappedPosition.quantityCounted = 1
        }
        else {
          mappedPosition.quantityCounted = mappedPosition.quantityCounted + 1
        }
        const newPosistions = [...positions]
        mappedPosition.enforceUpdate = true
        newPosistions[foundobj.index] = mappedPosition
        setPositions(newPosistions)
        if ((mappedPosition.article.useRFIDs || mappedPosition.article.useSerialKeys) && mappedPosition.quantityCounted > 0 && mappedPosition.quantityExpected > 1) {
          setSplitPositionId(mappedPosition.id)
        }
        return true
      }
    }
    throw new Error(t('picking:positionCouldNotBeAdded'))

  }, [setPositions, t, positions])

  useEffect(() => {
    if (splitPositionId) {
      splitPosition(splitPositionId)
      setSplitPositionId(null)
    }
  }, [setSplitPositionId, splitPosition, splitPositionId])

  const contextValues = {
    addPosition,
    copyPosition,
    splitPosition,
    loadReceiptData,
    submitAddReceipt,
    submitEditReceipt,
    submitStoreReceipt,
    formErrors, setFormErrors,
    resetErrorByFormKey,
    errorsOccurredOnSubmit,
    formDataHasChanged,
    resetFormData,
    setHeaderDataByKey,
    headerData,
    setPositionsByKey,
    setPositions,
    positions,
    actionMode, setActionMode,
    setSuccessSubmit,
    successSubmit,
    loadedReceiptData, checkInstance,
    storingResult, subsequentDeliveryResult, setSubsequentDeliveryResult, addScannedArticle
  }
  return (
    <LocalReceiptDataContext.Provider value={contextValues}>
      {children}
    </LocalReceiptDataContext.Provider>
  )
}

export default LocalReceiptDataProvider
